'use client';

import { AnimatePresence, LazyMotion, useDragControls } from 'motion/react';
import * as m from 'motion/react-m';
import { ReactNode, use, useContext, useMemo, useRef } from 'react';

import { DialogContext, Modal, ModalVariantProps, modalSlotRecipe } from '@flipster/react/design-system-legacy/modal';
import DraggableIcon from '@flipster/react/icon/generated/DraggableIcon';
import { Portal } from '@flipster/react/misc/portal';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { Styles, css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';

import { useCloseOnRouteChange, useSingleMovableModal } from './state';
import {
  MovableContext,
  constraintsStyle,
  getMovableDialogMotionProps,
  headerDragStyle,
  loadDomMax,
  useDragHandlePointerDown,
} from './utils';

interface RootProps {
  isOpen: boolean;
  onExit?: () => void;
  onClose?: () => void;
  type?: ModalVariantProps['type'];
  size?: ModalVariantProps['size'];
  scrollable?: boolean;
  css?: Styles;
  children: ReactNode;
}

const Root = (props: RootProps) => {
  const { isMobile } = useBreakpoint();
  useSingleMovableModal(props.isOpen, props.onClose);
  useCloseOnRouteChange(props.onClose);

  if (isMobile) {
    return (
      <Modal
        isOpen={props.isOpen}
        onExit={props.onExit}
        type={props.type}
        size={props.size}
        scrollable={props.scrollable}
        css={props.css}
      >
        {props.onClose && <Modal.Close onClose={props.onClose} />}
        {props.children}
      </Modal>
    );
  }

  return (
    <Portal selector='#modal'>
      <LazyMotion features={loadDomMax} strict>
        <DesktopLayout {...props} />
      </LazyMotion>
    </Portal>
  );
};

const DesktopLayout = ({
  type = 'normal',
  size = 'small',
  scrollable,
  css: cssProps,
  isOpen,
  onExit,
  onClose,
  children,
}: RootProps) => {
  const constraintsRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();

  const slots = useMemo(() => modalSlotRecipe.raw({ type, size, scrollable }), [type, size, scrollable]);
  const dialogCtxValue = useMemo(() => ({ slots }), [slots]);
  const movableCtxValue = useMemo(() => ({ dragControls }), [dragControls]);
  const handleCloseAreaPointerDown = useDragHandlePointerDown(dragControls);

  return (
    <DialogContext value={dialogCtxValue}>
      <MovableContext value={movableCtxValue}>
        <AnimatePresence onExitComplete={onExit}>
          {isOpen && (
            <div ref={constraintsRef} className={css(constraintsStyle)}>
              <m.section
                key='movable'
                className={css(slots.dialog, dialogOverride, cssProps)}
                {...getMovableDialogMotionProps(dragControls, constraintsRef)}
              >
                {onClose && (
                  <div
                    className={css(headerDragStyle, {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    })}
                    onPointerDown={handleCloseAreaPointerDown}
                  >
                    <DraggableIcon
                      size={24}
                      color={token('colors.content.default.level-2')}
                      className={css({ ml: 16, mt: 16 })}
                    />
                    <Modal.Close onClose={onClose} />
                  </div>
                )}
                {children}
              </m.section>
            </div>
          )}
        </AnimatePresence>
      </MovableContext>
    </DialogContext>
  );
};

const dialogOverride = {
  pos: 'absolute',
  top: '50%',
  left: '50%',
  pointerEvents: 'auto',
} as const;

interface BaseProps {
  css?: Styles;
  children: ReactNode;
}

const Header = ({ css: cssProps, children }: BaseProps) => {
  const movable = use(MovableContext);
  const { slots } = useContext(DialogContext);
  const handlePointerDown = useDragHandlePointerDown(movable?.dragControls);

  if (!movable) return <Modal.Header css={cssProps}>{children}</Modal.Header>;

  return (
    <header className={css(slots?.header, headerDragStyle, cssProps)} onPointerDown={handlePointerDown}>
      {children}
    </header>
  );
};

const Title = Modal.Title;
const Content = Modal.Content;
const Footer = Modal.Footer;

Root.displayName = 'MovableModalLegacy.Root';
Header.displayName = 'MovableModalLegacy.Header';

export { Root, Header, Title, Content, Footer };
